import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { LocalizedDatePipe } from 'src/app/angular-core/utils/pipes/localized-date/localized-date.pipe';
import { BasicXpacInfo, TelemetryData, TelemetryEventType } from 'src/app/generated/CrossPoint.RemoteXpac.Server';
import { DevicesActionsTypes, getDeviceTelemetryAction } from 'src/app/store/actions/devices.actions';
import { selectTelemetry } from 'src/app/store/selectors/devices.selectors';
import { IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';

const EVENT_REQUEST_COUNT: number[] = [100, 500, 1000, 10000];

@UntilDestroy()
@Component({
  selector: 'xpac-xpac-telemetry',
  templateUrl: './xpac-telemetry.component.html',
  styleUrls: ['./xpac-telemetry.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XpacTelemetryComponent implements OnInit {
  @Input() public xpac: BasicXpacInfo = null;
  
  public isLoading: boolean = true;
  public isLoadingMore: boolean = false;
  public hasMore: boolean = false;
  public telemetry: TelemetryData[] = null;
  
  private loadIndex: number = 0;

  constructor(
    private store: Store<IRemoteXPACPortalState>,
    private actions$: Actions,
    private datePipe: LocalizedDatePipe,
    private cdr: ChangeDetectorRef,
  ) {
  }

  public ngOnInit(): void {
    this.initListeners();
    this.store.dispatch(getDeviceTelemetryAction({
      data: { 
        deviceId: this.xpac.deviceId,
        maxEvents: EVENT_REQUEST_COUNT[this.loadIndex],
      },
    }));
  }

  public loadMore(): void {
    if (this.isLoadingMore || this.isLoadingMore) {
      return;
    }

    this.isLoadingMore = true;
    this.cdr.markForCheck();

    if (this.loadIndex < EVENT_REQUEST_COUNT.length - 1) {
      this.loadIndex++;
    }

    this.store.dispatch(getDeviceTelemetryAction({
      data: { 
        deviceId: this.xpac.deviceId,
        maxEvents: EVENT_REQUEST_COUNT[this.loadIndex],
      },
    }));
  }

  public getInfoString(log: TelemetryData): string {
    switch (log.type) {
      case TelemetryEventType.Trace:
        return 'Trace';
      case TelemetryEventType.Request:
        return `Request, ${log.duration} ms, HTTP ${log.resultCode ?? 'Unknown'}`;
      case TelemetryEventType.Dependency:
        return `Dependency, ${log.duration} ms, HTTP ${log.resultCode ?? 'Unknown'}`;
      case TelemetryEventType.Exception:
        return 'Exception';
    }
    return '';
  }
  
  public getSecondaryInfoString(log: TelemetryData): string {
    return this.datePipe.transform(log.timestamp, 'short');
  }

  private initListeners(): void {
    this.actions$
      .pipe(ofType(DevicesActionsTypes.GET_DEVICE_TELEMETRY_FAILED))
      .subscribe(() => {
        this.isLoadingMore = false;
        this.cdr.markForCheck();
      });

    this.store.select(selectTelemetry(this.xpac.deviceId))
      .pipe(
        filter(hasValue),
        untilDestroyed(this)
      )
      .subscribe((telemetry) => {
        this.telemetry = telemetry;
        this.isLoading = false;
        this.isLoadingMore = false;
        this.hasMore = telemetry.length >= EVENT_REQUEST_COUNT[this.loadIndex] && this.loadIndex < (EVENT_REQUEST_COUNT.length - 1);
        this.cdr.markForCheck();
      });
  }
}
